import type { QueryClient } from '@tanstack/react-query';
import type { RealtimeMessage } from './types';

/**
 * Maps push events to TanStack Query invalidations — client refetches via existing REST APIs.
 */
export function invalidateOnEvent(qc: QueryClient, message: RealtimeMessage): void {
  switch (message.type) {
    case 'NOTIFICATION_CREATED':
    case 'NOTIFICATION_READ':
    case 'NOTIFICATION_UNREAD_COUNT':
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      break;
    case 'APPROVAL_PENDING':
    case 'APPROVAL_RESOLVED':
      void qc.invalidateQueries({ queryKey: ['work-queue'] });
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['hr', 'employee-schedule-calendar'] });
      void qc.invalidateQueries({ queryKey: ['hr', 'shift-assignments'] });
      break;
    case 'ATTENDANCE_RECORDED':
    case 'ATTENDANCE_APPROVED':
      void qc.invalidateQueries({ queryKey: ['hr', 'attendance'] });
      void qc.invalidateQueries({ queryKey: ['hr', 'attendance-calendar'] });
      void qc.invalidateQueries({ queryKey: ['hr', 'overtime'] });
      break;
    case 'INVOICE_COMPLETED':
    case 'SHIFT_OPENED':
    case 'SHIFT_CLOSED':
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['pos'] });
      break;
    case 'INVENTORY_CHANGED':
      void qc.invalidateQueries({ queryKey: ['inventory'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      break;
    case 'DASHBOARD_STALE':
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      break;
    case 'POS_DISPLAY_UPDATED':
    case 'DESK_TICKET_UPDATED':
    case 'SOCIAL_MESSAGE_RECEIVED':
    case 'SOCIAL_MESSAGE_STATUS_CHANGED':
    case 'SOCIAL_CONVERSATION_UPDATED':
    case 'SOCIAL_CONVERSATION_ASSIGNED':
    case 'SOCIAL_CONVERSATION_TRANSFERRED':
    case 'SOCIAL_NOTE_CREATED':
      void qc.invalidateQueries({ queryKey: ['social', 'conversations'] });
      break;
    case 'SOCIAL_WHATSAPP_SCHEDULE_CREATED':
    case 'SOCIAL_WHATSAPP_SCHEDULE_UPDATED':
    case 'SOCIAL_WHATSAPP_SCHEDULE_PAUSED':
    case 'SOCIAL_WHATSAPP_SCHEDULE_EXECUTION_STARTED':
    case 'SOCIAL_WHATSAPP_SCHEDULE_EXECUTION_COMPLETED':
    case 'SOCIAL_WHATSAPP_SCHEDULE_EXECUTION_FAILED':
    case 'SOCIAL_WHATSAPP_SCHEDULE_NEXT_RUN_CHANGED':
      void qc.invalidateQueries({ queryKey: ['social', 'whatsapp', 'schedules'] });
      break;
    case 'SOCIAL_WHATSAPP_SYNC_STARTED':
    case 'SOCIAL_WHATSAPP_SYNC_PROGRESS':
    case 'SOCIAL_WHATSAPP_SYNC_RESOURCE_COMPLETED':
    case 'SOCIAL_WHATSAPP_SYNC_COMPLETED':
    case 'SOCIAL_WHATSAPP_SYNC_FAILED':
    case 'SOCIAL_WHATSAPP_SYNC_HEALTH_CHANGED':
      void qc.invalidateQueries({ queryKey: ['social', 'whatsapp', 'sync'] });
      break;
    case 'SOCIAL_WHATSAPP_WEBHOOK_DLQ':
    case 'SOCIAL_WHATSAPP_PHONE_QUALITY_CHANGED':
    case 'SOCIAL_WHATSAPP_ACCOUNT_HEALTH_CHANGED':
      void qc.invalidateQueries({ queryKey: ['social', 'whatsapp', 'webhook-events'] });
      break;
    case 'SOCIAL_WHATSAPP_SETTINGS_UPDATED':
    case 'SOCIAL_WHATSAPP_ACCOUNT_PROFILE_CHANGED':
    case 'SOCIAL_WHATSAPP_CREDENTIAL_EXPIRING':
    case 'SOCIAL_WHATSAPP_WEBHOOK_HEALTH_CHANGED':
      void qc.invalidateQueries({ queryKey: ['social', 'whatsapp', 'settings'] });
      break;
    case 'SOCIAL_ANALYTICS_UPDATED':
    case 'SOCIAL_WHATSAPP_ANALYTICS_UPDATED':
      void qc.invalidateQueries({ queryKey: ['social', 'whatsapp', 'analytics'] });
      void qc.invalidateQueries({ queryKey: ['social', 'analytics-center'] });
      break;
    case 'SOCIAL_WHATSAPP_REPORT_QUEUED':
    case 'SOCIAL_WHATSAPP_REPORT_PROGRESS':
    case 'SOCIAL_WHATSAPP_REPORT_READY':
    case 'SOCIAL_WHATSAPP_REPORT_FAILED':
    case 'SOCIAL_WHATSAPP_REPORT_EXPIRED':
      void qc.invalidateQueries({ queryKey: ['social', 'whatsapp', 'reports'] });
      break;
    case 'SOCIAL_WHATSAPP_AUDIT_EXPORT_READY':
    case 'SOCIAL_WHATSAPP_AUDIT_EXPORT_FAILED':
    case 'SOCIAL_WHATSAPP_AUDIT_INTEGRITY_ALERT':
      void qc.invalidateQueries({ queryKey: ['social', 'whatsapp', 'audit'] });
      break;
    case 'SOCIAL_WHATSAPP_INTEGRATION_HEALTH_CHANGED':
    case 'SOCIAL_WHATSAPP_INTEGRATION_SYNC_CHANGED':
      void qc.invalidateQueries({ queryKey: ['social', 'whatsapp', 'integrations'] });
      break;
    case 'SOCIAL_WHATSAPP_AI_SUGGESTION_READY':
    case 'SOCIAL_WHATSAPP_SUMMARY_READY':
      void qc.invalidateQueries({ queryKey: ['social', 'whatsapp', 'ai'] });
      break;
    case 'SOCIAL_WHATSAPP_KB_INGESTION_CHANGED':
      void qc.invalidateQueries({ queryKey: ['social', 'whatsapp', 'kb'] });
      break;
    case 'SOCIAL_WHATSAPP_SURVEY_RESPONSE_RECEIVED':
      void qc.invalidateQueries({ queryKey: ['social', 'whatsapp', 'surveys'] });
      break;
    case 'SOCIAL_MEDIA_PROCESSING':
    case 'SOCIAL_MEDIA_READY':
    case 'SOCIAL_MEDIA_REJECTED':
    case 'SOCIAL_MEDIA_DELETED':
      void qc.invalidateQueries({ queryKey: ['social', 'media-assets'] });
      void qc.invalidateQueries({ queryKey: ['social', 'media-folders'] });
      break;
    case 'SOCIAL_STORAGE_QUOTA_CHANGED':
      void qc.invalidateQueries({ queryKey: ['social', 'media-storage'] });
      void qc.invalidateQueries({ queryKey: ['social', 'media-assets'] });
      break;
    default:
      break;
  }
}
